import React from 'react';
import PropTypes from 'prop-types';
import { venuePropTypes, intlMessage } from 'constants/propTypes';
import { useIntl } from 'react-intl';
import { useFormContext, Controller } from 'react-hook-form';
import ReactPhoneNumberInput from 'react-phone-number-input';

import { countryCodeShortToSamplePhoneNumberNatl } from 'constants/countryCodeToStaticValueMaps';

import { bem } from 'helpers/class-name';

import Label from '../Label/Label';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { makeValidators } from '../helpers';
// CSS in _vendor-overrides.react-phone-number-input.scss

// This fallback is necessary to run tests in the Jest context, should be temporary
const contextFallback = () => null;
contextFallback.keys = () => [];
// The flag svgs aren't exported directly so we lens in to the folder and require by extension.
// We have a webpack module rule that sees this require, copies the results to our dist, and generates urls.
// We need to get one of those urls and turn it into a template for ReactPhoneNumberInput to use.
// Passing the first key (e.g. `./some/path/US.svg`) to the require.context yields the resolved url (e.g. `studios.cp.com/static/.../US.svg`).
const CountryFlagIconsContext = require?.context?.('country-flag-icons/3x2', false, /\.svg$/) || contextFallback;

const { block } = bem('hook-form-phone-input');

function PhoneNumberInput(props) {
  const {
    name,
    label,
    disabled,
    countryCode,
    optional,
    className,
    countries,
    addInternationalOption,
    placeholder: placeholderFromProps,
  } = props;

  const intl = useIntl();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const validators = makeValidators({ type: 'tel', ...props }, intl);
  const hasError = Object.keys(errors).includes(name);
  let placeholder;
  if (placeholderFromProps) {
    placeholder = intl.formatMessage(placeholderFromProps);
  } else {
    placeholder = countryCode ? countryCodeShortToSamplePhoneNumberNatl[countryCode] : '';
  }

  const firstFlag = CountryFlagIconsContext.keys()[0];
  const firstFilename = firstFlag?.split('/')?.slice(-1)[0];
  const flagUrl = CountryFlagIconsContext(firstFlag)?.replace(firstFilename, '{XX}.svg');

  return (
    <div className={block(className)}>
      <Label name={name} message={label} optional={optional} />
      <Controller
        control={control}
        name={name}
        rules={validators}
        // ⬇️ ReactPhoneNumberInput props ⬇️
        // `value` passed automatically
        // `onChange` passed automatically
        render={({ field }) => (
          <ReactPhoneNumberInput
            {...field}
            autoComplete="off"
            flagUrl={flagUrl}
            className={hasError ? 'PhoneInput--error' : ''}
            defaultCountry={countryCode}
            placeholder={placeholder}
            disabled={disabled}
            countries={countries}
            addInternationalOption={addInternationalOption}
          />
        )}
      />
      <ErrorMessage name={name} errors={errors} />
    </div>
  );
}

PhoneNumberInput.propTypes = {
  className: PropTypes.string,
  countryCode: PropTypes.string,
  disabled: PropTypes.bool,
  label: intlMessage,
  name: PropTypes.string,
  optional: PropTypes.bool,
  countries: PropTypes.arrayOf(venuePropTypes.countryCodeShort),
  addInternationalOption: PropTypes.bool,
  placeholder: intlMessage,
  validate: PropTypes.shape({
    value: PropTypes.func,
    message: intlMessage,
  }),
};

export default PhoneNumberInput;
